import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  FormInstance,
  FormItemProps,
  InputNumber,
  Space,
  Tooltip,
  TooltipProps,
  TreeSelect,
} from 'antd';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { XmSelect } from 'src/component/select/XmSelect';
import XmTimeZoneSelect from 'src/component/timezoneSelect/XmTimeZoneSelect';
import useReadonly from 'src/hook/useReadonly';
import { GLOB } from 'src/util/Glob';
import { isFieldDisabled, RecurrenceField } from './recurrenceFields';
import { RECURRENCE_HELP } from './recurrenceHelp';
import { RECURRENCE_TREE } from './recurrenceTree';
import { RECURRENCE_FREQUENCIES, RecurrenceFrequency, RecurrenceValues } from './types';

const FULL_WIDTH = { width: '100%' } as const;
const HELP_CURSOR = { cursor: 'help' } as const;
const HELP_TRIGGER: TooltipProps['trigger'] = ['hover', 'focus'];
const HELP_STYLES = { root: { maxWidth: 'none' } } as const;

const FREQUENCY_OPTIONS: { label: string; value: RecurrenceFrequency; }[] =
  RECURRENCE_FREQUENCIES.map((value) => ({
    value,
    label: `${value[0].toUpperCase()}${value.slice(1)}`,
  }));

const NUMBER_FIELDS: readonly { field: RecurrenceField; label: string; min: number; max: number; }[] = [
  { field: 'setpos', label: 'By Setpos number', min: -366, max: 366 },
  { field: 'week_number', label: 'By Week number', min: -53, max: 53 },
  { field: 'month_day', label: 'By Month day number', min: -31, max: 31 },
  { field: 'year_day', label: 'By Year day number', min: -366, max: 366 },
];

interface Props {
  readonly form: FormInstance<RecurrenceValues>;
  readonly values: RecurrenceValues;
}

interface HelpRowProps {
  readonly label: string;
  readonly name: keyof RecurrenceValues;
  readonly help: ReactNode;
  readonly rules?: FormItemProps['rules'];
  readonly required?: boolean;
  readonly initialValue?: RecurrenceValues[keyof RecurrenceValues];
}

const HelpRow: FC<PropsWithChildren<HelpRowProps>> = ({
  label,
  name,
  help,
  rules,
  required,
  initialValue,
  children,
}) => (
  <Form.Item label={label} required={required} htmlFor={name}>
    <Space.Compact block>
      <Form.Item name={name} rules={rules} initialValue={initialValue} noStyle>
        {children}
      </Form.Item>
      <Tooltip title={help} placement='right' trigger={HELP_TRIGGER} styles={HELP_STYLES}>
        <Button icon={<QuestionCircleOutlined />} aria-label='Field help' style={HELP_CURSOR} />
      </Tooltip>
    </Space.Compact>
  </Form.Item>
);

const XmRecurrenceFields: FC<Props> = ({ form, values }) => {
  const { isDisabled } = useReadonly();
  const disabled = (field: RecurrenceField): boolean =>
    isDisabled || isFieldDisabled(values, field);

  const handleHourChange = (value: number | null): void => {
    if (value === null || value === undefined) return;
    if (value < 0) form.setFieldValue('hour', 23);
    if (value > 23) form.setFieldValue('hour', 0);
  };

  return (
    <>
      <HelpRow
        label='Frequency'
        name='freq'
        help={RECURRENCE_HELP.freq}
        rules={[{ required: true, message: 'Please select Frequency!' }]}
        required
      >
        <XmSelect
          placeholder='Select Frequency'
          options={FREQUENCY_OPTIONS}
          style={FULL_WIDTH}
          ignoreReadonly
        />
      </HelpRow>
      <HelpRow
        label='Timezone'
        name='tzid'
        help={RECURRENCE_HELP.tzid}
        initialValue={GLOB.userInfo.timezone}
        required
      >
        <XmTimeZoneSelect local={GLOB.userInfo.timezone} style={FULL_WIDTH} />
      </HelpRow>
      <HelpRow
        label='By Hour'
        name='hour'
        help={RECURRENCE_HELP.hour}
        rules={[{ required: true, message: 'Please input By Hour!' }]}
        initialValue={0}
        required
      >
        <InputNumber
          placeholder='0 to 23'
          style={FULL_WIDTH}
          onChange={handleHourChange}
          disabled={isDisabled}
        />
      </HelpRow>
      <HelpRow label='By Weekday' name='weekday' help={RECURRENCE_HELP.weekday}>
        <TreeSelect {...RECURRENCE_TREE.day} disabled={disabled('weekday')} />
      </HelpRow>
      <HelpRow label='By Month' name='month' help={RECURRENCE_HELP.month}>
        <TreeSelect {...RECURRENCE_TREE.month} disabled={disabled('month')} />
      </HelpRow>
      {NUMBER_FIELDS.map(({ field, label, min, max }) => (
        <HelpRow key={field} label={label} name={field} help={RECURRENCE_HELP[field]}>
          <InputNumber
            min={min}
            max={max}
            placeholder={`${min} to ${max}`}
            style={FULL_WIDTH}
            disabled={disabled(field)}
          />
        </HelpRow>
      ))}
    </>
  );
};

export default XmRecurrenceFields;
